import {
  Select,
  Spin,
  Button,
  Input,
  message,
  Modal,
  Divider,
  Typography,
  Pagination,
} from 'antd'
import { useEffect, useState, useCallback } from 'react'
import {
  getComment,
  deleteCommentById,
  editComment,
  replyComment,
  addLike,
  unLike,
  updateAdminState,
  getCommentLike,
  addComment,
} from 'src/api/social'
import { getBots } from 'src/api/user'
import './Botpost.css'
import EmojiPicker from 'emoji-picker-react'
import { useParams, useLocation } from 'react-router-dom'
import { likeTypes, ADMINCHECKSTATE } from 'src/constants'
import { SmileOutlined, PlusOutlined } from '@ant-design/icons'
import { extractTextContent } from 'src/utils/stringUtil'
import { longToTimestamp } from 'src/utils/timestamp'
import Comment from 'src/components/Social/Comment'

const { confirm } = Modal
const { Text } = Typography

const ViewComment = () => {
  const { id } = useParams()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const [botUserId, setBotUserId] = useState(params.get('botUserId'))
  const [postText] = useState(params.get('text'))
  const [userId, setUserId] = useState(undefined)
  const [userData, setUserData] = useState([])
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [adminCheckState, setAdminCheckState] = useState('')
  const [modalVisible, setModalVisible] = useState(false)
  const [modalMode, setModalMode] = useState('add')
  const [modalRecord, setModalRecord] = useState(null)
  const [modalText, setModalText] = useState('')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [likeModalVisible, setLikeModalVisible] = useState(false)
  const [likeModalLoading, setLikeModalLoading] = useState(false)
  const [selectedCommentId, setSelectedCommentId] = useState(null)
  const [selectedLikeType, setSelectedLikeType] = useState(null)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [refreshCounters, setRefreshCounters] = useState({})

  const getRefreshReplies = useCallback((commentId) => {
    return () => {
      setRefreshCounters((prev) => ({
        ...prev,
        [commentId]: (prev[commentId] || 0) + 1,
      }))
    }
  }, [])

  const fetchBotUser = async () => {
    try {
      const response = await getBots()
      const usersList = response.data.map((element) => ({
        label: element.username,
        value: element.user_id,
      }))
      setUserData(usersList)
      if (usersList.length > 0) {
        setUserId(usersList[0].value)
      } else {
        message.error('No bot users available.')
      }
    } catch (error) {
      message.error('Failed to fetch users.')
    }
  }

  const transformData = async (data) => {
    if (!data) return []
    return await Promise.all(
      data.map(async (item) => {
        return {
          ...item,
          key: item.id,
          user_id: item.user?.user_id || 'unknown',
          username: item.user?.username || 'Unknown User',
          date: longToTimestamp(item.created_on),
          formattedText: item.deleted
            ? ''
            : extractTextContent(item.text || ''),
          comments: item.comments || 0,
          replies: [],
          deleted: item.deleted || false,
        }
      })
    )
  }

  const fetchComments = async (page, size, postId) => {
    try {
      setLoading(true)
      const response = await getComment(
        page - 1,
        size,
        postId,
        botUserId,
        adminCheckState
      )
      const transformedData = await transformData(response.data)
      setComments(transformedData)
      setTotalRecords(response.total_items)
    } catch (error) {
      message.error('Failed to fetch comments.')
    } finally {
      setLoading(false)
    }
  }

  const fetchCommentsState = async (page, size, postId, state) => {
    try {
      setLoading(true)
      const response = await getComment(
        page - 1,
        size,
        postId,
        botUserId,
        state
      )
      const transformedData = await transformData(response.data)
      setComments(transformedData)
      setTotalRecords(response.total_items)
    } catch (error) {
      message.error('Failed to fetch comments.')
    } finally {
      setLoading(false)
    }
  }

  const handleAddComment = async () => {
    if (!modalText.trim() || !userId) {
      message.error('Please select a user and enter a comment.')
      return
    }
    setLoading(true)
    const formattedContent = modalText.replace(
      /#(\w+)/g,
      '<hashtag>#$1</hashtag>'
    )
    try {
      await addComment(id, userId, formattedContent, postText, 'BOT-POST')
      setModalVisible(false)
      setModalText('')
      await fetchComments(currentPage, pageSize, id)
    } catch (error) {
      message.error('Failed to add comment.')
    } finally {
      setLoading(false)
      setShowEmojiPicker(false)
    }
  }

  const handleReply = (comment) => {
    if (comment.deleted) {
      message.error('Cannot reply to a deleted comment.')
      return
    }
    setModalMode('reply')
    setModalRecord(comment)
    setModalText('')
    setModalVisible(true)
  }

  const handleSaveReply = async () => {
    if (!modalRecord || !modalText.trim() || !userId) {
      message.error('Please select a user and enter a reply.')
      return
    }
    setLoading(true)
    const formattedContent = modalText.replace(
      /#(\w+)/g,
      '<hashtag>#$1</hashtag>'
    )
    try {
      await replyComment({
        postId: id,
        bot_user_id: userId,
        text: formattedContent,
        id: modalRecord.id,
      })
      setModalVisible(false)
      setModalText('')
      await fetchComments(currentPage, pageSize, id)
      if (modalRecord?.id) {
        setRefreshCounters((prev) => ({
          ...prev,
          [modalRecord.id]: (prev[modalRecord.id] || 0) + 1,
        }))
      }
    } catch (error) {
      message.error('Failed to add reply.')
    } finally {
      setLoading(false)
      setShowEmojiPicker(false)
    }
  }

  const handleEdit = (comment) => {
    if (comment.deleted) {
      message.error('Cannot edit a deleted comment.')
      return
    }
    setUserId(comment.user.user_id)
    setModalMode('edit')
    setModalRecord(comment)
    setModalText(comment.formattedText)
    setModalVisible(true)
  }

  const handleSaveEdit = async () => {
    if (!modalRecord || !modalText.trim() || !userId) {
      message.error('Please select a user and enter a comment.')
      return
    }
    setLoading(true)
    const formattedContent = modalText.replace(
      /#(\w+)/g,
      '<hashtag>#$1</hashtag>'
    )
    try {
      if (modalRecord.user_id === userId) {
        await editComment({
          id: modalRecord.id,
          text: formattedContent,
          userId: modalRecord.user_id,
          postId: id,
          bot_user_id: userId,
        })
        setModalVisible(false)
        setModalText('')
        await fetchComments(currentPage, pageSize, id)
      } else {
        message.error("Cannot edit another user's comment")
      }
    } catch (error) {
      message.error('Failed to update comment.')
    } finally {
      setLoading(false)
      setShowEmojiPicker(false)
    }
  }

  const handleDelete = (comment) => {
    confirm({
      title: 'Are you sure you want to delete this comment?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: async () => {
        try {
          setLoading(true)
          await deleteCommentById(comment)
          message.success('Comment deleted successfully!')
          const newTotalRecords = totalRecords - 1
          const newPage =
            currentPage > 1 && newTotalRecords % pageSize === 0
              ? currentPage - 1
              : currentPage
          await fetchComments(newPage, pageSize, id)
          setCurrentPage(newPage)
        } catch (error) {
          message.error(`Failed to delete comment: ${error.message}`)
        } finally {
          setLoading(false)
        }
      },
      onCancel: () => {
        message.info('Deletion cancelled.')
      },
    })
  }

  const handleLikeClick = async (record) => {
    if (record.deleted) {
      message.error('Cannot like a deleted comment.')
      return
    }
    setSelectedRecord(record)
    setSelectedLikeType(record.mine_like_type)
    setSelectedCommentId(record.id)
    setLikeModalVisible(true)
    setLikeModalLoading(true)
    try {
      const userReaction = await getCommentLike(record.id, userId)
      setSelectedLikeType(userReaction || undefined)
    } catch (error) {
      message.error('Failed to fetch like status.')
    } finally {
      setLikeModalLoading(false)
    }
  }

  const handleLikeSave = async () => {
    if (!selectedRecord || !userId) return
    setLoading(true)
    if (selectedRecord.user_id === userId) {
      message.error('Cannot react on your own comment')
      setLikeModalVisible(false)
      setLoading(false)
      return
    }
    try {
      const likeData = {
        ...selectedRecord,
        botUserId: userId,
        reaction: selectedLikeType,
      }
      selectedLikeType ? await addLike(likeData) : await unLike(likeData)
      await fetchComments(currentPage, pageSize, id)
      return true
    } catch (error) {
      message.error('Failed to update like.')
    } finally {
      setLoading(false)
      setLikeModalVisible(false)
    }
  }

  const handleAdminStateChange = async (value, record) => {
    setLoading(true)
    try {
      await updateAdminState('COMMENT', record, value)
      await fetchComments(currentPage, pageSize, id)
      return true
    } catch (error) {
      message.error('Failed to update admin state.')
    } finally {
      setLoading(false)
    }
  }

  const handleEmojiSelect = (emojiObject) => {
    const textarea = document.getElementById(
      'modalTextArea'
    ) as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd

    setModalText((modalText) => {
      const textBefore = modalText.slice(0, start)
      const textAfter = modalText.slice(end)

      return textBefore + emojiObject.emoji + textAfter
    })
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start + emojiObject.emoji.length
      textarea.focus()
    }, 0)
  }
  const handleUserChangeLike = async (e) => {
    setUserId(e)
    setLikeModalLoading(true)
    try {
      const userReaction = await getCommentLike(selectedCommentId, e)
      setSelectedLikeType(userReaction || undefined)
    } catch (error) {
      message.error('Failed to fetch like status.')
    } finally {
      setLikeModalLoading(false)
    }
  }

  useEffect(() => {
    fetchBotUser()
  }, [])

  useEffect(() => {
    fetchComments(currentPage, pageSize, id)
  }, [currentPage, pageSize, botUserId])

  return (
    <Spin spinning={loading}>
      <div style={{ padding: '15px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Select
            placeholder="Select State"
            onChange={(value) => {
              setAdminCheckState(value)
              fetchCommentsState(currentPage, pageSize, id, value)
            }}
            value={adminCheckState || undefined}
            options={ADMINCHECKSTATE}
            allowClear
            style={{ width: '15%' }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setModalMode('add')
              setModalVisible(true)
              setModalText('')
              setModalRecord(null)
            }}
            style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
          >
            Add Comment
          </Button>
        </div>
        <Divider />
        {comments.map((comment) => (
          <Comment
            key={comment.id}
            comment={comment}
            userId={userId}
            userData={userData}
            onReply={handleReply}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onLike={handleLikeClick}
            onAdminStateChange={handleAdminStateChange}
            refreshReplies={getRefreshReplies(comment.id)}
          />
        ))}
        <div
          className="mt-8 flex justify-between items-center"
          style={{ marginTop: 10 }}
        >
          {totalRecords > 0 && (
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              onChange={(page, size) => {
                setCurrentPage(page)
                setPageSize(size)
              }}
              pageSizeOptions={['10', '25', '50', '100']}
              showSizeChanger
            />
          )}
        </div>
      </div>
      <Modal
        title={
          modalMode === 'edit'
            ? 'Edit Comment'
            : modalMode === 'reply'
            ? 'Reply to Comment'
            : 'Add Comment'
        }
        visible={modalVisible}
        onOk={() => {
          if (modalMode === 'edit') handleSaveEdit()
          else if (modalMode === 'reply') handleSaveReply()
          else handleAddComment()
        }}
        onCancel={() => {
          setModalVisible(false)
          setModalText('')
          setShowEmojiPicker(false)
        }}
        okText={
          modalMode === 'edit'
            ? 'Save'
            : modalMode === 'reply'
            ? 'Reply'
            : 'Add'
        }
      >
        <div style={{ position: 'relative' }}>
          <Select
            placeholder="Username"
            allowClear
            options={userData}
            onChange={(value) => setUserId(value)}
            value={userId}
            style={{ width: '100%', marginBottom: '10px' }}
            showSearch
            optionFilterProp="label"
          />
          <Input.TextArea
            id="modalTextArea"
            rows={4}
            value={modalText}
            onChange={(e) => setModalText(e.target.value)}
            placeholder={
              modalMode === 'edit'
                ? 'Edit text here...'
                : modalMode === 'reply'
                ? 'Write a reply...'
                : 'Write a comment...'
            }
            style={{ marginBottom: '10px' }}
          />
          <Button
            type="text"
            icon={<SmileOutlined />}
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            style={{
              backgroundColor: '#ffe0bd',
              color: 'black',
              borderRadius: '8px',
              border: '1px solid #ccc',
              padding: '5px',
            }}
          />
          {showEmojiPicker && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                marginTop: '5px',
                zIndex: 10,
                background: 'white',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
              }}
            >
              <EmojiPicker onEmojiClick={handleEmojiSelect} />
            </div>
          )}
        </div>
      </Modal>
      <Modal
        title="React Comment"
        visible={likeModalVisible}
        onOk={handleLikeSave}
        onCancel={() => {
          setLikeModalVisible(false)
          setSelectedLikeType(null)
        }}
      >
        <Spin spinning={likeModalLoading}>
          <Select
            placeholder="Username"
            options={userData}
            onChange={handleUserChangeLike}
            value={userId}
            style={{ width: '100%', marginBottom: '10px' }}
            showSearch
            optionFilterProp="label"
          />
          <Select
            placeholder="Select Like Type"
            options={likeTypes}
            value={selectedLikeType}
            onChange={(value) => setSelectedLikeType(value)}
            style={{ width: '100%' }}
            allowClear
          />
        </Spin>
      </Modal>
    </Spin>
  )
}

export default ViewComment
